<template>
  <div class="linha-turnos-table d-flex flex-column pa-2">
    <v-alert v-if="!linha || !linha.id_linha" type="info" dense outlined>
      {{ $t('crudLinhas.detail.selectLine') }}
    </v-alert>
    <template v-else>
      <v-form v-if="canEdit" ref="formTurnosPassageiros" v-model="formValidadoTurnosPassageiros">
        <v-row dense align="center">
          <v-col cols="12" sm="5">
            <LazySelect
              ref="turnoSelect"
              get-url="getTurnoslazy"
              text="descricao_turno"
              value="id_turno"
              :label="$t('crudLinhas.form.shifts.self')"
              :request-data="turnoLazyRequestData"
              @input="onTurnoSelect"
            />
          </v-col>
          <v-col cols="12" sm="5">
            <v-text-field
              v-model="itemTurnosPassageiros.lotacao"
              :label="$t('crudLinhas.form.shifts.passengersCapacity')"
              type="number"
              dense
              outlined
              hide-details="auto"
              :rules="[(v) => v >= 0 || $t('crudLinhas.validation.capacityMustBeGreaterThanZero')]"
            />
          </v-col>
          <v-col cols="12" sm="2" class="text-sm-right">
            <v-btn
              color="primary"
              dark
              small
              fab
              :loading="saving"
              :disabled="saving"
              @click="saveTurnosPassageiros"
            >
              <v-icon v-if="!editingTurnosPassageiros">mdi-plus</v-icon>
              <v-icon v-else>mdi-pencil</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <merus-data-table
        class="mt-3"
        :headers="headersTurnosPassageiros"
        :items="turnosPassageirosLinha"
        :loading="loading"
        :loading-text="$t('crudLinhas.loading')"
        :default-multi-sort="defaultMultiSortTurnosPassageiros"
        cod-tabela="LINHAS__TURNOS_PASSAGEIROS"
        @update:headers="headersTurnosPassageirosConfig = $event"
      >
        <template v-slot:toolbar>
          <cadastro-toolbar
            :title="$t('crudLinhas.form.shifts.title')"
            :token="token"
            :items="turnosPassageirosLinha"
            :csv-title="$t('crudLinhas.form.shifts.csvTitle')"
            :pdf-title="$t('crudLinhas.form.shifts.pdfTitle')"
            :pdf-headers="headersTurnosPassageiros"
            :pdf-filename="$t('crudLinhas.form.shifts.pdfFilename')"
            hide-search
          />
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon
            v-if="canEdit"
            small
            class="mr-2"
            color="green"
            @click="editItemTurnosPassageiros(item)"
          >mdi-pencil</v-icon>
          <v-icon
            v-if="canEdit"
            small
            color="red"
            @click="deleteItemTurnosPassageiros(item)"
          >mdi-delete</v-icon>
        </template>
      </merus-data-table>
    </template>
  </div>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";
import LazySelect from "@/components/Selects/LazySelect.vue";
import MerusDataTable from "@/components/Tables/MerusDataTable.vue";
import CadastroToolbar from "@/components/Toolbars/CadastroToolbar.vue";

export default {
  name: "LinhaTurnosTable",
  components: {
    LazySelect,
    MerusDataTable,
    CadastroToolbar,
  },
  props: {
    linha: { type: Object, default: null },
    token: { type: String, default: null },
    canEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      saving: false,
      turnosPassageirosLinha: [],
      headersTurnosPassageirosConfig: [],
      itemTurnosPassageiros: {
        id_turno: null,
        lotacao: null,
      },
      defaultItemTurnosPassageiros: {
        id_turno: null,
        lotacao: null,
      },
      formValidadoTurnosPassageiros: false,
      editingTurnosPassageiros: false,
      unidadeIds: [],
    };
  },
  computed: {
    headersTurnosPassageiros() {
      if (this.headersTurnosPassageirosConfig.length) {
        return this.headersTurnosPassageirosConfig;
      }
      return [
        { text: this.$t("crudLinhas.headers.shifts.code"), value: "cod_turno" },
        { text: this.$t("crudLinhas.headers.shifts.description"), value: "descricao_turno" },
        { text: this.$t("crudLinhas.headers.shifts.capacity"), value: "lotacao" },
        { text: this.$t("crudLinhas.headers.shifts.actions"), value: "actions", sortable: false },
      ];
    },
    turnoLazyRequestData() {
      return {
        id_unidades: (this.unidadeIds || [])
          .map((id) => Number(id))
          .filter((n) => Number.isFinite(n) && n > 0),
      };
    },
    defaultMultiSortTurnosPassageiros() {
      return [
        { value: "cod_turno", options: { text: this.$t("system.labels.increasing"), value: 1 } },
      ];
    },
  },
  watch: {
    linha: {
      immediate: true,
      deep: true,
      handler(linha) {
        if (linha && linha.id_linha != null) {
          this.unidadeIds = this.parseUnidadeIds(linha);
          this.loadTurnos();
        } else {
          this.unidadeIds = [];
          this.turnosPassageirosLinha = [];
          this.resetForm();
        }
      },
    },
    unidadeIds: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          const ref = this.$refs.turnoSelect;
          if (ref && typeof ref.loadLazyItems === "function") {
            ref.loadLazyItems(ref.lazySearch || "");
          }
        });
      },
    },
  },
  methods: {
    parseUnidadeIds(linha) {
      const ids = new Set();
      const add = (raw) => {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) ids.add(n);
      };
      if (Array.isArray(linha.unidades)) {
        linha.unidades.forEach((u) => {
          if (u && typeof u === "object" && u.id_unidade != null) add(u.id_unidade);
          else add(u);
        });
      } else if (linha.unidades != null && String(linha.unidades).trim() !== "") {
        String(linha.unidades).split(",").forEach((part) => add(String(part).trim()));
      }
      if (linha.ids_unidades != null && String(linha.ids_unidades).trim() !== "") {
        String(linha.ids_unidades).split(",").forEach((part) => add(String(part).trim()));
      }
      return Array.from(ids);
    },
    resetForm() {
      this.itemTurnosPassageiros = Object.assign({}, this.defaultItemTurnosPassageiros);
      this.editingTurnosPassageiros = false;
      this.$nextTick(() => this.syncTurnoSelect(null));
      if (this.$refs.formTurnosPassageiros) {
        this.$refs.formTurnosPassageiros.resetValidation();
      }
    },
    async loadTurnos() {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha) return;
      this.loading = true;
      try {
        const res = await apiController.request(apiController.endpoints.getTurnospassageiroporlinha, {
          params: { idlinha: idLinha },
          silent: true,
        });
        this.turnosPassageirosLinha = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.turnosPassageirosLinha = [];
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.loading = false;
      }
    },
    onTurnoSelect(val) {
      this.itemTurnosPassageiros.id_turno = val;
    },
    syncTurnoSelect(item) {
      const ref = this.$refs.turnoSelect;
      if (!ref) return;
      if (item && item.id_turno != null) {
        ref.cacheSelectedItems(
          [{
            id_turno: item.id_turno,
            descricao_turno: item.descricao_turno,
            cod_turno: item.cod_turno,
            id_unidade: item.id_unidade,
          }],
          "id_turno"
        );
        ref.setData(item.id_turno);
      } else {
        ref.resetData();
      }
    },
    async saveTurnosPassageiros() {
      if (!this.canEdit || this.saving) return;
      this.$refs.formTurnosPassageiros.validate();
      if (!this.formValidadoTurnosPassageiros) return;

      const next = this.turnosPassageirosLinha.slice();
      const draft = Object.assign({}, this.itemTurnosPassageiros);

      if (this.editingTurnosPassageiros) {
        const idx = next.findIndex((a) => a.id_turno === draft.id_turno);
        if (idx >= 0) next.splice(idx, 1, draft);
      } else {
        const ref = this.$refs.turnoSelect;
        const turno = ref && ref.selectedItemCache
          ? ref.selectedItemCache[draft.id_turno]
          : null;
        draft.id_linha = this.linha.id_linha;
        draft.cod_turno = turno ? turno.cod_turno : null;
        draft.descricao_turno = turno ? turno.descricao_turno : null;
        next.push(draft);
      }

      await this.persistTurnos(next);
      this.resetForm();
    },
    editItemTurnosPassageiros(item) {
      this.itemTurnosPassageiros = Object.assign({}, item);
      this.editingTurnosPassageiros = true;
      this.$nextTick(() => this.syncTurnoSelect(item));
    },
    async deleteItemTurnosPassageiros(item) {
      if (!this.canEdit || this.saving) return;
      const next = this.turnosPassageirosLinha.filter((t) => t.id_turno !== item.id_turno);
      await this.persistTurnos(next);
    },
    async persistTurnos(turnos) {
      const idLinha = this.linha && this.linha.id_linha;
      if (!idLinha) return;
      this.saving = true;
      try {
        await apiController.request(apiController.endpoints.deleteTurnoslinhabulk, {
          data: { id_linha: idLinha },
          silent: true,
        });
        const payload = (turnos || []).map((t) => [idLinha, t.id_turno, t.lotacao]);
        if (payload.length) {
          const res = await apiController.request(apiController.endpoints.insertTurnoslinhabulk, {
            data: payload,
            silent: true,
          });
          if (res.data && res.data.erro) {
            throw new Error(res.data.mensagem || "Erro ao salvar turnos");
          }
        }
        this.turnosPassageirosLinha = turnos || [];
        this.$emit("changed");
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
        await this.loadTurnos();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
